import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
  PDFViewer,
  pdf,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { getAllDispatch } from "../../utils/api";
import { useParams } from "react-router-dom";
import Loading from "../../components/Loading";
import { toast } from "react-toastify";

const styles = StyleSheet.create({
  page: { padding: 15, fontSize: 8 },
  row: { flexDirection: "row", borderBottom: "1px solid #ddd", alignItems: "center" },
  headingRow: { flexDirection: "row", backgroundColor: "#fff101", borderBottom: "2px solid #000" },
  cell: { flex: 1, padding: 4, textAlign: "left" },
  headerCell: { flex: 1, padding: 4, fontWeight: "bold", textAlign: "left" },
  dateCell: { flex: 1.2, padding: 4 },
  qntCell: { flex: 0.6, padding: 4, textAlign: "right" },
});

const formatDescription = (desc, note) => {
  return [desc, note].filter(Boolean).join(" - ") || "-";
};

const MyDoc = ({ list, params }) => (
  <Document>
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View style={styles.headingRow}>
        <Text style={[styles.headerCell, styles.dateCell]}>Date</Text>
        <Text style={styles.headerCell}>Customer</Text>
        <Text style={styles.headerCell}>City</Text>
        <Text style={styles.headerCell}>Description</Text>
        <Text style={styles.headerCell}>Location</Text>
        <Text style={styles.headerCell}>Phone</Text>
        <Text style={styles.headerCell}>Item</Text>
        <Text style={[styles.headerCell, styles.qntCell]}>Qty</Text>
      </View>
      {list?.map((record, rIdx) =>
        record?.orders?.map((item, oIdx) => (
          <View key={`${rIdx}-${oIdx}`} style={styles.row}>
            <Text style={[styles.cell, styles.dateCell]}>
              {new Date(item?.updatedAt).toLocaleString("en-IN")}
            </Text>
            <Text style={styles.cell}>{item?.customer?.userName || "-"}</Text>
            <Text style={styles.cell}>{item?.group || "-"}</Text>
            <Text style={styles.cell}>{formatDescription(item?.description, item?.dispatchNote)}</Text>
            <Text style={styles.cell}>{item?.customer?.location || "-"}</Text>
            <Text style={styles.cell}>{record?._id || "-"}</Text>
            <Text style={styles.cell}>{item?.product?.productName || "-"}</Text>
            <Text style={[styles.cell, styles.qntCell]}>{item?.quantity || 0}</Text>
          </View>
        ))
      )}
    </Page>
  </Document>
);

const DispatchPdf = () => {
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const params = useParams();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getData = async () => {
    setIsLoading(true);
    const [success, data] = await getAllDispatch();
    if (success) {
      setList(data);
    } else {
      toast.error("Failed to load dispatch data");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [params.id]);

  const downloadPdf = () => {
    pdf(<MyDoc list={list} params={params} />)
      .toBlob()
      .then((blob) => saveAs(blob, "Dispatch-Report.pdf"));
  };

  if (isLoading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header bar */}
      <div className="p-3 bg-white border-b shadow-sm flex items-center justify-between sticky top-0 z-50 print:hidden">
        <h1 className="text-base font-bold text-gray-800">Dispatch Report</h1>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition active:scale-95"
          >
            🖨️ Print
          </button>
          <button
            onClick={downloadPdf}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs transition active:scale-95"
          >
            📥 Download PDF
          </button>
        </div>
      </div>

      {isMobile ? (
        <div className="p-2 overflow-x-auto">
          <table className="min-w-full text-xs bg-white border border-gray-200">
            <thead className="bg-yellow-300">
              <tr>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Customer</th>
                <th className="p-2 border">City</th>
                <th className="p-2 border">Description</th>
                <th className="p-2 border">Location</th>
                <th className="p-2 border">Phone</th>
                <th className="p-2 border">Item</th>
                <th className="p-2 border">Qty</th>
              </tr>
            </thead>
            <tbody>
              {list?.map((record, rIdx) =>
                record?.orders?.map((item, oIdx) => (
                  <tr key={`${rIdx}-${oIdx}`} className="hover:bg-gray-50 border-b">
                    <td className="p-2 border whitespace-nowrap">
                      {new Date(item?.updatedAt).toLocaleString("en-IN")}
                    </td>
                    <td className="p-2 border font-semibold">{item?.customer?.userName}</td>
                    <td className="p-2 border">{item?.group}</td>
                    <td className="p-2 border">{formatDescription(item?.description, item?.dispatchNote)}</td>
                    <td className="p-2 border">{item?.customer?.location}</td>
                    <td className="p-2 border">{record?._id}</td>
                    <td className="p-2 border font-bold">{item?.product?.productName}</td>
                    <td className="p-2 border font-black text-right">{item?.quantity}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex-1 w-full h-[90vh]">
          <PDFViewer style={{ width: "100%", height: "100%" }}>
            <MyDoc list={list} params={params} />
          </PDFViewer>
        </div>
      )}
    </div>
  );
};

export default DispatchPdf;
